import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tooltip,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Block as BlockIcon,
  CheckCircle as ActiveIcon,
  AttachMoney as DividendIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { useApp } from '../App';
import { businessesApi, beneficiariesApi, jurisdictionsApi, holdersApi } from '../services/api';

const BUSINESS_TYPES = [
  { value: 'LLC', label: 'ООО' },
  { value: 'JSC', label: 'АО' },
  { value: 'SOLE_PROP', label: 'ИП' },
  { value: 'PARTNERSHIP', label: 'Партнёрство' },
  { value: 'HOLDING', label: 'Холдинг' },
  { value: 'FUND', label: 'Фонд' },
  { value: 'TRUST', label: 'Траст' },
  { value: 'OTHER', label: 'Другое' },
];

const BUSINESS_STATUSES = [
  { value: 'ACTIVE', label: 'Активный', color: 'success' },
  { value: 'DORMANT', label: 'Неактивный', color: 'warning' },
  { value: 'LIQUIDATING', label: 'Ликвидация', color: 'error' },
  { value: 'SOLD', label: 'Продан', color: 'default' },
];

const CURRENCIES = ['USD', 'EUR', 'RUB', 'GBP', 'CHF', 'AED', 'CNY'];

const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

export default function Businesses() {
  const { showNotification } = useApp();
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [businesses, setBusinesses] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dividendDialogOpen, setDividendDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [jurisdictions, setJurisdictions] = useState([]);
  const [holders, setHolders] = useState([]);
  const [summary, setSummary] = useState(null);
  const [dividendForm, setDividendForm] = useState({ amount: '', date: '' });

  const [form, setForm] = useState({
    code: '',
    name: '',
    legalName: '',
    businessType: 'LLC',
    industry: '',
    registrationNumber: '',
    taxId: '',
    registrationDate: '',
    ownershipPercent: 100,
    investedAmount: '',
    investedCurrency: 'USD',
    currentValuation: '',
    valuationCurrency: 'USD',
    valuationDate: '',
    valuationMethod: '',
    annualRevenue: '',
    annualProfit: '',
    employeeCount: '',
    website: '',
    address: '',
    ceo: '',
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    status: 'ACTIVE',
    notes: '',
    beneficiaryId: '',
    jurisdictionId: '',
    holderId: '',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [bizRes, benefRes, jurisRes, holdersRes, summaryRes] = await Promise.all([
        businessesApi.getAll({ search }),
        beneficiariesApi.getAll({ isActive: true }),
        jurisdictionsApi.getAll({ isActive: true }),
        holdersApi.getAll({ isActive: true }),
        businessesApi.getSummary(),
      ]);

      setBusinesses(bizRes.data);
      setBeneficiaries(benefRes.data);
      setJurisdictions(jurisRes.data);
      setHolders(holdersRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      showNotification('Ошибка загрузки данных', 'error');
    } finally {
      setLoading(false);
    }
  }, [search, showNotification]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDialogOpen = (item = null) => {
    if (item) {
      setEditItem(item);
      setForm({
        code: item.code || '',
        name: item.name || '',
        legalName: item.legalName || '',
        businessType: item.businessType || 'LLC',
        industry: item.industry || '',
        registrationNumber: item.registrationNumber || '',
        taxId: item.taxId || '',
        registrationDate: item.registrationDate || '',
        ownershipPercent: item.ownershipPercent || 100,
        investedAmount: item.investedAmount || '',
        investedCurrency: item.investedCurrency || 'USD',
        currentValuation: item.currentValuation || '',
        valuationCurrency: item.valuationCurrency || 'USD',
        valuationDate: item.valuationDate || '',
        valuationMethod: item.valuationMethod || '',
        annualRevenue: item.annualRevenue || '',
        annualProfit: item.annualProfit || '',
        employeeCount: item.employeeCount || '',
        website: item.website || '',
        address: item.address || '',
        ceo: item.ceo || '',
        contactPerson: item.contactPerson || '',
        contactEmail: item.contactEmail || '',
        contactPhone: item.contactPhone || '',
        status: item.status || 'ACTIVE',
        notes: item.notes || '',
        beneficiaryId: item.beneficiaryId || '',
        jurisdictionId: item.jurisdictionId || '',
        holderId: item.holderId || '',
      });
    } else {
      setEditItem(null);
      setForm({
        code: '',
        name: '',
        legalName: '',
        businessType: 'LLC',
        industry: '',
        registrationNumber: '',
        taxId: '',
        registrationDate: '',
        ownershipPercent: 100,
        investedAmount: '',
        investedCurrency: 'USD',
        currentValuation: '',
        valuationCurrency: 'USD',
        valuationDate: '',
        valuationMethod: '',
        annualRevenue: '',
        annualProfit: '',
        employeeCount: '',
        website: '',
        address: '',
        ceo: '',
        contactPerson: '',
        contactEmail: '',
        contactPhone: '',
        status: 'ACTIVE',
        notes: '',
        beneficiaryId: '',
        jurisdictionId: '',
        holderId: '',
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editItem) {
        await businessesApi.update(editItem.id, form);
        showNotification('Бизнес обновлён', 'success');
      } else {
        await businessesApi.create(form);
        showNotification('Бизнес создан', 'success');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error saving business:', error);
      showNotification(error.response?.data?.error || 'Ошибка сохранения', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить этот бизнес?')) return;
    try {
      await businessesApi.delete(id);
      showNotification('Бизнес удалён', 'success');
      loadData();
    } catch (error) {
      console.error('Error deleting business:', error);
      showNotification('Ошибка удаления', 'error');
    }
  };

  const handleToggleActive = async (item) => {
    try {
      if (item.isActive) {
        await businessesApi.deactivate(item.id);
        showNotification('Бизнес деактивирован', 'info');
      } else {
        await businessesApi.activate(item.id);
        showNotification('Бизнес активирован', 'success');
      }
      loadData();
    } catch (error) {
      console.error('Error toggling status:', error);
      showNotification('Ошибка изменения статуса', 'error');
    }
  };

  const handleDividendOpen = (item) => {
    setEditItem(item);
    setDividendForm({ amount: '', date: new Date().toISOString().split('T')[0] });
    setDividendDialogOpen(true);
  };

  const handleDividendSubmit = async () => {
    try {
      await businessesApi.recordDividend(editItem.id, dividendForm.amount, dividendForm.date);
      showNotification('Дивиденд зафиксирован', 'success');
      setDividendDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error recording dividend:', error);
      showNotification('Ошибка записи дивиденда', 'error');
    }
  };

  // Chart data
  const industryChartData = summary?.byIndustry
    ? Object.entries(summary.byIndustry)
        .map(([industry, data]) => ({
          name: industry,
          value: data.valuation,
          count: data.count,
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 8)
    : [];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Бизнесы
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            placeholder="Поиск..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <IconButton onClick={loadData}>
            <RefreshIcon />
          </IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleDialogOpen()}>
            Добавить
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={2}>
          <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
            <CardContent>
              <Typography variant="overline">Всего</Typography>
              <Typography variant="h4">{summary?.totalBusinesses || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card sx={{ bgcolor: 'secondary.main', color: 'white' }}>
            <CardContent>
              <Typography variant="overline">Оценка</Typography>
              <Typography variant="h5">{formatCurrency(summary?.totalValuation)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">Инвестировано</Typography>
              <Typography variant="h5">{formatCurrency(summary?.totalInvested)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">Прирост</Typography>
              <Typography variant="h5" color={summary?.totalGain >= 0 ? 'success.main' : 'error.main'}>
                {formatCurrency(summary?.totalGain)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">Дивиденды</Typography>
              <Typography variant="h5" color="info.main">
                {formatCurrency(summary?.totalDividends)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card>
            <CardContent>
              <Typography variant="overline" color="text.secondary">Выручка</Typography>
              <Typography variant="h5">{formatCurrency(summary?.totalRevenue)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Chart and Table */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: 350 }}>
            <Typography variant="h6" gutterBottom>По отраслям</Typography>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={industryChartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={(v) => `$${(v / 1000000).toFixed(1)}M`} />
                <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11 }} />
                <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="value" fill="#1B365D" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Код</TableCell>
                  <TableCell>Название</TableCell>
                  <TableCell>Тип</TableCell>
                  <TableCell>Отрасль</TableCell>
                  <TableCell align="right">Доля</TableCell>
                  <TableCell align="right">Оценка</TableCell>
                  <TableCell>Бенефициар</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell align="center">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {businesses.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{item.code}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.name}</Typography>
                      {item.legalName && (
                        <Typography variant="caption" color="text.secondary">{item.legalName}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={BUSINESS_TYPES.find(t => t.value === item.businessType)?.label || item.businessType}
                      />
                    </TableCell>
                    <TableCell>{item.industry || '-'}</TableCell>
                    <TableCell align="right">{item.ownershipPercent}%</TableCell>
                    <TableCell align="right">
                      {formatCurrency(item.currentValuation, item.valuationCurrency)}
                    </TableCell>
                    <TableCell>{item.Beneficiary?.name || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={BUSINESS_STATUSES.find(s => s.value === item.status)?.label || item.status}
                        color={BUSINESS_STATUSES.find(s => s.value === item.status)?.color || 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Редактировать">
                        <IconButton size="small" onClick={() => handleDialogOpen(item)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Записать дивиденд">
                        <IconButton size="small" onClick={() => handleDividendOpen(item)}>
                          <DividendIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={item.isActive ? 'Деактивировать' : 'Активировать'}>
                        <IconButton size="small" onClick={() => handleToggleActive(item)}>
                          {item.isActive ? <BlockIcon fontSize="small" /> : <ActiveIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Удалить">
                        <IconButton size="small" onClick={() => handleDelete(item.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {businesses.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography color="text.secondary" sx={{ py: 3 }}>
                        Нет бизнесов
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      {/* Edit/Create Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {editItem ? 'Редактировать бизнес' : 'Добавить бизнес'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Basic Info */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom>Основная информация</Typography>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Код"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Краткое название"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Полное юридическое название"
                value={form.legalName}
                onChange={(e) => setForm({ ...form, legalName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Тип</InputLabel>
                <Select
                  value={form.businessType}
                  label="Тип"
                  onChange={(e) => setForm({ ...form, businessType: e.target.value })}
                >
                  {BUSINESS_TYPES.map(t => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Отрасль"
                value={form.industry}
                onChange={(e) => setForm({ ...form, industry: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Рег. номер"
                value={form.registrationNumber}
                onChange={(e) => setForm({ ...form, registrationNumber: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="ИНН"
                value={form.taxId}
                onChange={(e) => setForm({ ...form, taxId: e.target.value })}
              />
            </Grid>

            {/* Ownership */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 1 }}>Владение</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Бенефициар</InputLabel>
                <Select
                  value={form.beneficiaryId}
                  label="Бенефициар"
                  onChange={(e) => setForm({ ...form, beneficiaryId: e.target.value })}
                >
                  <MenuItem value="">Не указан</MenuItem>
                  {beneficiaries.map(b => (
                    <MenuItem key={b.id} value={b.id}>{b.name} ({b.code})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Юрисдикция</InputLabel>
                <Select
                  value={form.jurisdictionId}
                  label="Юрисдикция"
                  onChange={(e) => setForm({ ...form, jurisdictionId: e.target.value })}
                >
                  <MenuItem value="">Не указана</MenuItem>
                  {jurisdictions.map(j => (
                    <MenuItem key={j.id} value={j.id}>{j.name} ({j.code})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Через структуру</InputLabel>
                <Select
                  value={form.holderId}
                  label="Через структуру"
                  onChange={(e) => setForm({ ...form, holderId: e.target.value })}
                >
                  <MenuItem value="">Напрямую</MenuItem>
                  {holders.map(h => (
                    <MenuItem key={h.id} value={h.id}>{h.name} ({h.code})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="number"
                label="Доля (%)"
                value={form.ownershipPercent}
                onChange={(e) => setForm({ ...form, ownershipPercent: e.target.value })}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Дата регистрации"
                value={form.registrationDate}
                onChange={(e) => setForm({ ...form, registrationDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Статус</InputLabel>
                <Select
                  value={form.status}
                  label="Статус"
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  {BUSINESS_STATUSES.map(s => (
                    <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Valuation */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 1 }}>Оценка</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Инвестировано"
                value={form.investedAmount}
                onChange={(e) => setForm({ ...form, investedAmount: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Валюта</InputLabel>
                <Select
                  value={form.investedCurrency}
                  label="Валюта"
                  onChange={(e) => setForm({ ...form, investedCurrency: e.target.value })}
                >
                  {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Текущая оценка"
                value={form.currentValuation}
                onChange={(e) => setForm({ ...form, currentValuation: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Валюта</InputLabel>
                <Select
                  value={form.valuationCurrency}
                  label="Валюта"
                  onChange={(e) => setForm({ ...form, valuationCurrency: e.target.value })}
                >
                  {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Дата оценки"
                value={form.valuationDate}
                onChange={(e) => setForm({ ...form, valuationDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label="Метод оценки"
                value={form.valuationMethod}
                onChange={(e) => setForm({ ...form, valuationMethod: e.target.value })}
                placeholder="DCF, P/E, Book Value..."
              />
            </Grid>

            {/* Financials */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 1 }}>Финансы</Typography>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Годовая выручка"
                value={form.annualRevenue}
                onChange={(e) => setForm({ ...form, annualRevenue: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                label="Годовая прибыль"
                value={form.annualProfit}
                onChange={(e) => setForm({ ...form, annualProfit: e.target.value })}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                fullWidth
                type="number"
                label="Сотрудников"
                value={form.employeeCount}
                onChange={(e) => setForm({ ...form, employeeCount: e.target.value })}
              />
            </Grid>

            {/* Contacts */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ mt: 1 }}>Контакты</Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Адрес"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Сайт"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="CEO"
                value={form.ceo}
                onChange={(e) => setForm({ ...form, ceo: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Контактное лицо"
                value={form.contactPerson}
                onChange={(e) => setForm({ ...form, contactPerson: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Email"
                value={form.contactEmail}
                onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Телефон"
                value={form.contactPhone}
                onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Примечания"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editItem ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dividend Dialog */}
      <Dialog open={dividendDialogOpen} onClose={() => setDividendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Записать дивиденд - {editItem?.name}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Текущие накопленные дивиденды: {formatCurrency(editItem?.dividendsReceived, editItem?.valuationCurrency)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Сумма дивиденда"
                value={dividendForm.amount}
                onChange={(e) => setDividendForm({ ...dividendForm, amount: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Дата"
                value={dividendForm.date}
                onChange={(e) => setDividendForm({ ...dividendForm, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDividendDialogOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={handleDividendSubmit}>
            Записать
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
